// Live's Talking Head with Advanced on (issue 223, `talking-head-form/schema.ts` `advancedSchema`,
// `hooks/useSubmitTalkingHead.ts` `submitAdvanced`; capture design-ref/studio-depth/07c): each
// actor speaks the script in its voice, directed by the vibe, about the product if one is chosen.
// One `video.talking_head_advanced` job per actor (a8's G8-talking-head-advanced).
import type {
  CatalogVideoModel,
  Job,
  VideoTalkingHeadAdvancedRequest as Advanced,
} from "../../../../api/generated/model";
import { VideoTalkingHeadAdvancedRequest } from "../../../../api/generated/zod";
import { MIN_SCRIPT, voiceOf, type Cast } from "./cast";
import { useGroupedStarts, type ActorStart } from "./use-priced-starts";

export const ADVANCED_TYPE = "video.talking_head_advanced";

/** Live's first "Vibe / Direction" line (`placeholders.ts` `VIBE_PLACEHOLDERS`), shown whole. */
export const VIBE_PLACEHOLDER =
  "High-energy morning routine in a messy kitchen, excited and casual, like a real user " +
  "filming on their phone — fast, natural jump cuts.";

/** The catalog's Advanced rows: live's one, Kling v3 Pro, locked in the form. */
export function advancedModels(models: readonly CatalogVideoModel[]): CatalogVideoModel[] {
  return models.filter((each) => each.job_types.includes(ADVANCED_TYPE));
}

/** What the Advanced form holds besides its actors. */
export interface AdvancedDraft {
  script: string;
  vibe: string;
  productId: string | null;
}

/**
 * Each actor's request, or none until live's rules hold (`advancedSchema`): an actor at least,
 * each with a voice ("All avatars must have a voice selected"), a script of ten characters
 * ("Script is required") and a vibe ("Vibe is required"). Live sends the script and the vibe as
 * typed, and leaves the clips' model, the ratio and the resolution at the form's "Auto": the
 * server's Kling v3 Pro, 9:16 and the clips' own.
 */
export function advancedStartsOf(
  model: CatalogVideoModel | undefined,
  casts: readonly Cast[],
  draft: AdvancedDraft,
): ActorStart<Advanced>[] | undefined {
  if (model === undefined || casts.length === 0) return undefined;
  if (draft.script.trim().length < MIN_SCRIPT || draft.vibe.trim() === "") return undefined;
  const starts: ActorStart<Advanced>[] = [];
  for (const cast of casts) {
    const voice = voiceOf(cast);
    if (voice === null) return undefined;
    const result = VideoTalkingHeadAdvancedRequest.safeParse({
      type: ADVANCED_TYPE,
      model: model.id,
      actor: cast.actor.source,
      voice_id: voice.id,
      script: draft.script,
      vibe: draft.vibe,
      ...(draft.productId === null ? {} : { product_id: draft.productId }),
    });
    if (!result.success) return undefined;
    starts.push({ actorId: cast.actor.key, data: result.data });
  }
  return starts;
}

/**
 * Live's Advanced start: a job per actor, one `group` for them all, unquoted as live's (its
 * chip shows the bolt alone): the run is free, each of its steps holds its own price as it
 * starts, and a team short of credits is refused at the start (a8's G8-talking-head-advanced).
 */
export function useAdvancedStarts(options: {
  teamId: string;
  brandId: string;
  model: CatalogVideoModel | undefined;
  casts: readonly Cast[];
  draft: AdvancedDraft;
  onStarted: (job: Job) => void;
}) {
  const { teamId, brandId, model, casts, draft, onStarted } = options;
  return useGroupedStarts({
    teamId,
    brandId,
    starts: advancedStartsOf(model, casts, draft),
    onStarted,
    unquoted: true,
  });
}
